/**
 * VoiceOrb Component
 *
 * 3D morphing orb that reacts to voice with liquid animations.
 * Uses Framer Motion for smooth state transitions.
 */

import React from 'react';
import { motion } from 'framer-motion';
import './VoiceOrb.css';

const VoiceOrb = ({
  state = 'idle', // idle, connecting, listening, speaking, processing, error, muted
  audioLevel = 0, // 0-1 range for audio reactivity
  onClick,
  disabled = false
}) => {
  // State-based gradient colors - Zoomey Theme
  const gradients = {
    idle: 'linear-gradient(145deg, #7f8c8d, #95a5a6)',  // Border color gray
    connecting: 'linear-gradient(145deg, #fa5400, #e67e22)', // Orange
    listening: 'linear-gradient(145deg, #009689, #00796b)',  // Teal
    speaking: 'linear-gradient(145deg, #3498db, #2980b9)',   // Keep blue
    processing: 'linear-gradient(145deg, #fa5400, #e67e22)', // Orange
    error: 'linear-gradient(145deg, #e7000b, #c0392b)',      // Danger red
    muted: 'linear-gradient(145deg, #6c757d, #495057)'       // Muted gray
  };

  // State-based glow colors - Zoomey Theme
  const glowColors = {
    idle: 'rgba(127, 140, 141, 0.3)',      // Border color gray
    connecting: 'rgba(250, 84, 0, 0.5)',    // Orange
    listening: 'rgba(0, 150, 137, 0.6)',    // Teal
    speaking: 'rgba(52, 152, 219, 0.6)',    // Blue
    processing: 'rgba(250, 84, 0, 0.5)',    // Orange
    error: 'rgba(231, 0, 11, 0.5)',         // Danger red
    muted: 'rgba(108, 117, 125, 0.4)'       // Muted gray
  };

  // Framer Motion variants for different states
  const orbVariants = {
    idle: {
      scale: 1,
      rotate: 0,
      transition: { duration: 0.5, ease: 'easeInOut' }
    },
    connecting: {
      scale: [1, 1.05, 1],
      rotate: 360,
      transition: {
        scale: { repeat: Infinity, duration: 1.2, ease: 'easeInOut' },
        rotate: { repeat: Infinity, duration: 3, ease: 'linear' }
      }
    },
    listening: {
      scale: 1 + audioLevel * 0.2,
      transition: { duration: 0.1, ease: 'easeOut' }
    },
    speaking: {
      scale: [1, 1.08, 1],
      transition: {
        scale: { repeat: Infinity, duration: 0.6, ease: 'easeInOut' }
      }
    },
    processing: {
      scale: 1,
      rotate: 360,
      transition: {
        rotate: { repeat: Infinity, duration: 2, ease: 'linear' }
      }
    },
    error: {
      x: [0, -8, 8, -8, 8, 0],
      transition: { duration: 0.5, ease: 'easeInOut' }
    },
    muted: {
      scale: 1,
      opacity: 0.8,
      transition: { duration: 0.3, ease: 'easeInOut' }
    }
  };

  // Get icon based on state
  const getIcon = () => {
    switch (state) {
      case 'muted':
        return (
          <svg viewBox="0 0 24 24" className="orb-icon">
            <path fill="currentColor" d="M19 11h-1.7c0 .74-.16 1.43-.43 2.05l1.23 1.23c.56-.98.9-2.09.9-3.28zm-4.02.17c0-.06.02-.11.02-.17V5c0-1.66-1.34-3-3-3S9 3.34 9 5v.18l5.98 5.99zM4.27 3L3 4.27l6.01 6.01V11c0 1.66 1.33 3 2.99 3 .22 0 .44-.03.65-.08l1.66 1.66c-.71.33-1.5.52-2.31.52-2.76 0-5.3-2.1-5.3-5.1H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c.91-.13 1.77-.45 2.54-.9L19.73 21 21 19.73 4.27 3z"/>
          </svg>
        );
      case 'connecting':
        return (
          <svg viewBox="0 0 24 24" className="orb-icon spin">
            <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="2" strokeDasharray="31.4 31.4" />
          </svg>
        );
      case 'listening':
        return (
          <svg viewBox="0 0 24 24" className="orb-icon">
            <path fill="currentColor" d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z"/>
            <path fill="currentColor" d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"/>
          </svg>
        );
      case 'speaking':
        return (
          <svg viewBox="0 0 24 24" className="orb-icon pulse">
            <path fill="currentColor" d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
          </svg>
        );
      case 'processing':
        return (
          <svg viewBox="0 0 24 24" className="orb-icon">
            <circle cx="12" cy="12" r="3" fill="currentColor"/>
            <path fill="currentColor" d="M12 2v3m0 14v3m10-10h-3M5 12H2m15.07-5.07l-2.12 2.12M8.05 15.95l-2.12 2.12m11.14 0l-2.12-2.12M8.05 8.05L5.93 5.93"/>
          </svg>
        );
      case 'error':
        return (
          <svg viewBox="0 0 24 24" className="orb-icon">
            <path fill="currentColor" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
          </svg>
        );
      default:
        return (
          <svg viewBox="0 0 24 24" className="orb-icon">
            <path fill="currentColor" d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z"/>
            <path fill="currentColor" d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"/>
          </svg>
        );
    }
  };

  // Calculate dynamic glow intensity
  const glowIntensity = state === 'listening' ? 20 + audioLevel * 40 : 20;

  return (
    <div className="voice-orb-container">
      {/* SVG Filter for liquid morphing effect */}
      <svg style={{ position: 'absolute', width: 0, height: 0 }}>
        <defs>
          <filter id="liquid-morph">
            <feTurbulence
              type="fractalNoise"
              baseFrequency="0.015"
              numOctaves="2"
              result="noise"
            />
            <feDisplacementMap
              in="SourceGraphic"
              in2="noise"
              scale={state === 'listening' ? 3 + audioLevel * 8 : 3}
              xChannelSelector="R"
              yChannelSelector="G"
            />
          </filter>
        </defs>
      </svg>

      {/* Glow rings */}
      <div className={`orb-glow-ring ring-1 ${state}`} />
      <div className={`orb-glow-ring ring-2 ${state}`} />
      <div className={`orb-glow-ring ring-3 ${state}`} />

      {/* Main orb */}
      <motion.button
        className={`voice-orb-main ${state} ${state !== 'idle' && state !== 'error' ? 'morphing' : ''}`}
        onClick={onClick}
        disabled={disabled || state === 'connecting'}
        variants={orbVariants}
        animate={state}
        style={{
          background: gradients[state],
          boxShadow: `0 0 ${glowIntensity}px ${glowColors[state]}`,
          '--audio-level': audioLevel
        }}
        whileHover={{ scale: state === 'idle' ? 1.05 : undefined }}
        whileTap={{ scale: 0.95 }}
      >
        {/* Inner highlight */}
        <div className="orb-inner-highlight" />

        {/* Center icon */}
        <div className="orb-icon-wrapper">
          {getIcon()}
        </div>

        {/* Audio bars for listening */}
        {state === 'listening' && (
          <div className="orb-audio-bars">
            {[...Array(5)].map((_, i) => (
              <span
                key={i}
                style={{
                  animationDelay: `${i * 0.1}s`,
                  height: `${8 + audioLevel * 20 * (1 + Math.sin(i))}px`
                }}
              />
            ))}
          </div>
        )}

        {/* Pulse waves for speaking */}
        {state === 'speaking' && (
          <div className="orb-pulse-waves">
            <span className="pulse-wave" />
            <span className="pulse-wave" style={{ animationDelay: '0.3s' }} />
            <span className="pulse-wave" style={{ animationDelay: '0.6s' }} />
          </div>
        )}
      </motion.button>
    </div>
  );
};

export default VoiceOrb;
